// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { bool, func, shape, string } from 'prop-types';
import {
  Divider,
  Grid,
  withStyles,
} from '@material-ui/core';

import styles from './styles';
import { getI18n } from '../../../../utils/intl';
import {
  getProxyOrganizations,
  getApiPlans,
  getRateLimitQuota,
  getIsDeployRateLimitFlag,
} from '../../../../reducers/proxy';
import { getKeyStoreDetails } from '../../../../reducers/keyStore';
import { getFeatureFlagApiPlans } from '../../../../reducers/portalConfig';
import {
  DetailsPanelContainer,
  Footer,
} from '../../../../components';
import PortalCompatibility from './portalCompatibility';
import Apis from './apis';
import ApiKeys from './apiKeys';
import ApiPlans from './apiPlans';
import Plans from './plans';
import Organizations from './organizations';
import ProxyURLContainer from "./ProxyURLContainer";

export const Overview = (props) => {
  const {
    classes,
    featureFlagApiPlans,
    rateLimitQuota,
    proxyUuid,
    proxyStatus,
    proxyDetails,
    parsedGatewayDetails,
    isCompatible,
    proxyOrganizations = {},
    push,
    notifyMessages,
    proxyUrl,
    proxyPort,
    handleUrlChange,
    proxy,
    isCurrentTab,
  } = props;

  const intl = getI18n(useIntl());
  const { tenantGatewayLogs } = parsedGatewayDetails;
  let otkVersion = get(tenantGatewayLogs, 'otk_version');
  if (isEmpty(otkVersion)) {
    otkVersion = intl.getI18nMessage(
      'label.proxy.details.page.overview.otk.not.installed');
  }
  const gatewayVersion = get(tenantGatewayLogs, 'version');

  const isUseNewAutoSync = get(proxyDetails, 'config.useNewAutoSync') === true;

  const onDeployRateLimit = () => {
    props.deployRateLimits({ proxyUuid });
  }

  const onRateLimitDeployTimerExpiry = () => {
    props.fetchProxyOrganizations(proxyUuid);
    props.fetchRateLimitQuota(proxyUuid);
  }

  useEffect(() => {
    props.fetchProxyOrganizations(proxyUuid);
    props.fetchRateLimitQuota(proxyUuid);
  }, []);

  useEffect(() => {
    if(get(proxy, 'keyStoreUuid') && isCurrentTab)
      props.fetchKeyStore(get(proxy, 'keyStoreUuid'));
  }, [proxyDetails, isCurrentTab]);

  return (
    <div
      id="proxy-details-overview-container"
      data-apim-test="proxy-details-overview-container"
      data-layer7-test="proxy-details-overview-container"
    >
      <Grid
        container
        id="metadata-top-panel-container"
        data-apim-test="metadata-top-panel-container"
        data-layer7-test="metadata-top-panel-container"
        className={classes.topPanelContainer}
      >
        <Grid item md={3} sm={12} xs={12}>
          <ProxyURLContainer
            id="overview-proxy-url"
            title={intl.getI18nMessage('label.proxy.details.page.overview.proxy.url.with.port')}
            proxyUrl={proxyUrl}
            portNumber={proxyPort}
            notifyMessages={notifyMessages}
            copyMessage={intl.getI18nMessage('label.proxy.details.proxy.url.copied')}
            onUpdate={handleUrlChange}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <DetailsPanelContainer
            id="overview-otk-version"
            title={intl.getI18nMessage('label.proxy.details.page.overview.otk.version')}
            subTitle={otkVersion}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <DetailsPanelContainer
            id="overview-gateway-version"
            title={intl.getI18nMessage('label.proxy.details.page.overview.gateway.version')}
            subTitle={gatewayVersion}
          />
        </Grid>
        <Grid item md={3} sm={12} xs={12}>
          <PortalCompatibility
            isCompatible={isCompatible}
          />
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      <Grid container className={classes.marginPanelContainer}>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Apis
            {...props}
          />
        </Grid>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <ApiKeys
            {...props}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.marginPanelContainer}>
        {/*<Grid item md={4} sm={4} xs={12} className={classes.paper}>*/}
        {/*  <DetailsPaper*/}
        {/*    id="gateway-bundles"*/}
        {/*    title={intl.getI18nMessage('label.gateway.bundles')}*/}
        {/*    content={'0'}*/}
        {/*    lastUpdated={Date.now()}*/}
        {/*  />*/}
        {/*</Grid>*/}
        <Grid item md={4} sm={4} xs={12} className={classes.paper}>
          <Plans
            id="account-plans"
            title={intl.getI18nMessage('label.rate.limit.quotas')}
            subTitle={intl.getI18nMessage('label.account.plans.previous')}
            proxyStatus={proxyStatus}
            portalPlanCounts={get(rateLimitQuota, 'definedEntityCount')}
            gatewayPlanCounts={get(rateLimitQuota, 'deployedEntityCount')}
            lastUpdated={get(rateLimitQuota, 'lastTimeDeployed')}
            onDeployRateLimit={onDeployRateLimit}
            onRateLimitDeployTimerExpiry={onRateLimitDeployTimerExpiry}
            isDeployRateLimitDisabled={!isUseNewAutoSync}
          />
        </Grid>
        {featureFlagApiPlans &&
          <Grid item md={4} sm={4} xs={12} className={classes.paper}>
            <ApiPlans
              {...props}
            />
          </Grid>
        }
        {proxyOrganizations.totalElements &&
          <Grid item md={4} sm={4} xs={12} className={classes.paper}>
            <Organizations
              proxyStatus={proxyStatus}
              proxyOrganizations={proxyOrganizations}
              push={push}
            />
          </Grid>
        }
      </Grid>
      <Footer />
    </div>
  );
};

const mapStateToProps = state => ({
  featureFlagApiPlans: getFeatureFlagApiPlans(state),
  proxyOrganizations: getProxyOrganizations(state),
  apiPlans: getApiPlans(state),
  rateLimitQuota: getRateLimitQuota(state),
  isDeployRateLimitFlag: getIsDeployRateLimitFlag(state),
  keyStoreDetails: getKeyStoreDetails(state),
});

Overview.propTypes = {
  classes: shape({}),
  featureFlagApiPlans: bool,
  proxyUuid: string,
  proxyDetails: shape({}),
  rateLimitQuota: shape({}),
  proxyStatus: string,
  parsedGatewayDetails: shape({}),
  isCompatible: bool,
  proxyOrganizations: shape({}),
  apiPlans: shape({}),
  push: func,
  notifyMessages: func,
  fetchApiPlans: func,
  fetchProxyOrganizations: func,
  fetchRateLimitQuota: func,
  handleUrlChange: func,
  proxyUrl: string,
  proxyPort: string,
  isDeployRateLimitFlag: bool,
  deployRateLimits: func,
  keyStoreDetails: shape({}),
  proxy: shape({}),
  fetchKeyStore: func,
  isCurrentTab: bool,
};
Overview.displayName = 'Overview';

export default compose(
  withStyles(styles),
  connect(mapStateToProps),
)(Overview);
